import configs from '../configs';


// Exported for LayoutHeader: the logout button opens the Microsoft logout
// in a new tab (user-gesture context) on mobile devices, where the chained
// same-tab redirect gets stuck on the "You signed out" page.
export function isMobileDevice() {
  return (
    /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent))
  );
}

/**
 * Build the Microsoft end-session URL used by the chained MS logout
 * (LoginKeycloak redirects to it after Keycloak logout).
 *
 * Per MSAL's logout docs, Microsoft redirects back to post_logout_redirect_uri
 * as long as: the URI is registered as a reply url on the app registration,
 * the URI is provided in the request, and the user has an active session with
 * the identity provider.
 *
 * client_id identifies the app so Microsoft trusts the registered
 * post_logout_redirect_uri — per OIDC spec, when post_logout_redirect_uri is
 * sent without id_token_hint, the OP may only redirect to a URI registered
 * for the identified client.
 *
 * logout_hint (the login user's email) makes Microsoft sign out that session
 * directly, skipping the account-picker page. The picker flow is where the
 * post_logout_redirect_uri redirect gets dropped — the user picks an account,
 * gets signed out, then stays stuck on Microsoft's "You signed out" page.
 * Passing logout_hint replicates the reliable id_token_hint path used when an
 * MSAL account exists. If the hint doesn't match an active session, Microsoft
 * falls back to the account picker, so omitting/mismatching it degrades
 * gracefully. Per Microsoft docs, logout_hint works best when the login_hint
 * optional claim is enabled on the app registration.
 * id_token_hint (when available) additionally forces the redirect per the
 * OIDC RP-Initiated Logout spec.
 * post_logout_redirect_uri must exactly match a registered redirect URI —
 * pass the bare origin, no trailing slash.
 */
export function buildMsLogoutUrl({
  authority,
  clientId,
  origin,
  logoutHint,
  idTokenHint,
}) {
  const params = [
    `client_id=${encodeURIComponent(clientId)}`,
    `post_logout_redirect_uri=${encodeURIComponent(origin)}`,
  ];
  if (idTokenHint) {
    params.push(`id_token_hint=${encodeURIComponent(idTokenHint)}`);
  }
  if (logoutHint) {
    params.push(`logout_hint=${encodeURIComponent(logoutHint)}`);
  }
  return `${authority}/oauth2/v2.0/logout?${params.join('&')}`;
}

/**
 * Detect an MSAL redirect response in the current URL (query string or
 * fragment). MSAL responses carry 'state' AND 'client_info' parameters;
 * Keycloak's callback only carries 'code'/'state'. Used to tell apart the
 * return trip of the login-time MSAL silent auth (loginRedirect
 * prompt='none') from the Keycloak auth-code callback.
 */
export function isMsalAuthResponseInUrl() {
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(
    window.location.hash.replace(/^#/, ''),
  );
  return (
    (params.has('state') && params.has('client_info')) ||
    (hashParams.has('state') && hashParams.has('client_info'))
  );
}

export async function performMsalLogout(msalInstance, {
  accounts = [],
  user = {},
  onClearLogin,
  onNavigateHome,
  forcePopup = false,
} = {}) {
  // Force popup mode — used as a fallback when the main logout flow throws
  if (forcePopup) {
    try {
      await msalInstance.logoutPopup({
        mainWindowRedirectUri: window.location.origin,
      });
      await msalInstance.clearCache();
    } catch {
      // Swallow errors — this is already a fallback path
    } finally {
      onClearLogin?.();
    }
    return;
  }

  const isSSOUser = user?.objectId || user?.unique_id;
  const isMobile = isMobileDevice();

  if (isSSOUser || accounts.length > 0 || configs.env !== 'local') {
    if (isMobile && typeof msalInstance.logoutPopup === 'function') {
      sessionStorage.setItem('userLogout', true);
      sessionStorage.removeItem('user');
      try {
        await msalInstance.logoutPopup({
          mainWindowRedirectUri: window.location.origin,
        });
        await msalInstance.clearCache();
        onClearLogin?.();
      } catch {
        window.location.href = '/';
        onClearLogin?.();
      }
    } else {
      // Await logoutRedirect to ensure the browser navigation to Microsoft's
      // logout endpoint is fully initiated before any subsequent code runs.
      // Without await, onClearLogin (which triggers React re-render via
      // removeUser) can interfere with the pending navigation.
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: '/',
      });
      onClearLogin?.();
    }
  } else {
    onClearLogin?.();
    onNavigateHome?.();
  }
}
